<template>
  <div>
    <!-- Button to open the short ship dialog -->
    <button class="btn btn-primary" @click="shortShipDialogVisible = true;">Short Shipped</button>
    <!-- Short ship dialog -->
    <el-dialog :visible.sync="shortShipDialogVisible" width="66%">
      <template slot="title">
        <h4>Select Items Short Shipped</h4>
      </template>
      <div class="short-ship-form">
        <!-- Loop through filtered items to display them -->
        <div v-for="product in filteredItems" class="row">
          <div class="col-xs-3" style="text-align: center;">
            <!-- Input number for short ship quantity -->
            <el-input-number class="short-ship-quantity" v-model="items[product.id]" :min="0"
                             :max="product.quantity"></el-input-number>
          </div>
          <!-- Display product information if available -->
          <template v-if="productInfo[product.sku]">
            <div class="col-xs-2">
              <template v-if="productInfo[product.sku].image">
                <img v-if="productInfo[product.sku].image"
                     :src="productInfo[product.sku].image.src.replace('.png', '_200x.png')"
                     :alt="productInfo[product.sku].image.alt"
                     class="img-responsive"/>
              </template>
            </div>
            <div class="col-xs-3">
              <div>{{ productInfo[product.sku].title }}</div>
              <div>{{ product.sku }}</div>
            </div>
          </template>
        </div>
        <!-- Option to create RA if applicable -->
        <div class="col-xs-12 m-l-3" v-if="isRA">
          <el-switch v-model="createRA" active-text="Create RA"></el-switch>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <!-- Cancel and submit buttons -->
        <button :disabled="loading" class="btn btn-danger" @click="shortShipDialogVisible = false;">Cancel</button>
        <button :disabled="loading" class="btn btn-primary" @click="submit">Submit</button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios, {RepositoryError} from "../../../repositories";
import Decimal from 'decimal.js';
import {extractD365Item, notify} from '../../../utils';
import _ from 'lodash';
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {
  ORDER_DISCOUNT_REMOVE_CODE,
  ORDER_DRAFT_SAVE,
  ORDER_GET_PRODUCTS_SUBTOTAL,
  ORDER_PAYMENT_REFRESH,
  ORDER_PRODUCTS_UPDATE_PRICE,
  ORDER_PRODUCTS_UPDATE_QUANTITY,
  ORDER_SET_NOTE,
  ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE,
  ORDER_SHIPPING_CUSTOM_SELECT,
  ORDER_SHIPPING_SELECT
} from "../../../store/types";
import {
  ORDER_RETURN_CREATE_DRAFT_RA,
  ORDER_RETURN_FULL_REFUND,
  ORDER_RETURN_REFUND,
  ORDER_RETURN_SET_NOTE,
  ORDER_RETURN_SET_PRODUCT_UPDATE,
  ORDER_RETURN_SET_REASON_CODE,
  ORDER_RETURN_SET_REASON_CODE_DETAIL,
  ORDER_RETURN_SET_REMARKS,
  ORDER_RETURN_SET_TEXT_REMARKS,
  ORDER_RETURN_SKIP_RA
} from "../../../store/modules/order/return/types";

export default {
  name: 'OrderShortShip',
  props: {
    order: {
      required: true,
      type: Object,
    },
    products: {
      required: true,
      type: Object,
    },
    region: {
      required: true,
      type: String
    },
    d365Order: {
      required: true,
      type: Object
    }
  },
  data() {
    const isRA = this.$route.path.match(/order-return/);
    const filteredItems = [];
    let items = {};
    this.order.line_items.forEach(i => {
      items[i.id] = 0;
      const product = _.pickBy(this.products.items, j => j.variant.sku === i.sku);
      const productId = Object.keys(product)[0];
      if (isRA) {
        const d365Item = extractD365Item(i, this.d365Order.lines, this.products.items[productId]);
        if (d365Item.SalesOrderLineStatus !== 'Invoiced') {
          filteredItems.push(i);
        }
      } else {
        filteredItems.push(i);
      }
    });
    return {
      filteredItems,
      shortShipDialogVisible: false,
      items,
      createRA: true,
      loading: false,
      isRA
    }
  },
  computed: {
    productInfo() {
      return _.keyBy(this.products.items, function (i) {
        return i.variant.sku;
      })
    },
    ...mapState({
      auth: state => state.auth,
      customOptions: state => state.order.shipping.customOptions
    }),
    ...mapGetters({
      subtotal: ORDER_GET_PRODUCTS_SUBTOTAL,
    }),
  },
  methods: {
    ...mapActions({
      setProductQuantity: ORDER_PRODUCTS_UPDATE_QUANTITY,
      setProductPrice: ORDER_PRODUCTS_UPDATE_PRICE,
      setSelected: ORDER_SHIPPING_SELECT,
      setCustomSelected: ORDER_SHIPPING_CUSTOM_SELECT,
      refreshPayment: ORDER_PAYMENT_REFRESH,
      createDraftOrder: ORDER_DRAFT_SAVE,
      calculatePartialRefund: ORDER_RETURN_REFUND,
      calculateFullRefund: ORDER_RETURN_FULL_REFUND,
      createDraftRA: ORDER_RETURN_CREATE_DRAFT_RA,
      skipRA: ORDER_RETURN_SKIP_RA,
      removeDiscount: ORDER_DISCOUNT_REMOVE_CODE
    }),
    ...mapMutations({
      setNote: ORDER_SET_NOTE,
      setReturnNote: ORDER_RETURN_SET_NOTE,
      setCustomSelectedPrice: ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE,
      updateReturnProduct: ORDER_RETURN_SET_PRODUCT_UPDATE,
      setRemarks: ORDER_RETURN_SET_REMARKS,
      setTextRemarks: ORDER_RETURN_SET_TEXT_REMARKS,
      setReasonCode: ORDER_RETURN_SET_REASON_CODE,
      setReasonCodeDetail: ORDER_RETURN_SET_REASON_CODE_DETAIL
    }),
    /**
     * Submits the short ship form.
     * Sends a request to the server with the short shipped items and handles the response.
     * @returns {Promise<void>}
     */
    async submit() {
      this.loading = true;

      try {
        await axios.post('/orders/short-shipped', {
          sales_channel: this.region,
          order: this.order,
          lineitems: _.pickBy(this.items, i => i > 0),
        });

        notify('Short Ship notification sent.', 'success');

        if (this.isRA) {
          await this.handleRAA();
        } else if (this.$route.path.match(/order-change/)) {
          await this.handleOCA();
        }

        this.shortShipDialogVisible = false;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Handles the order change action.
     * Updates product prices and creates a draft order.
     * @returns {Promise<void>}
     */
    async handleOCA() {
      const skus = [];
      await this.removeDiscount();
      for (const [lineItemId, quantity] of Object.entries(this.items)) {
        const product = _.pickBy(this.products.items, i => i.line_item.id == lineItemId);
        const productId = Object.keys(product)[0];
        if (quantity > 0) skus.push(`${quantity}x ` + product[productId].line_item.sku);

        const unitPrice = (new Decimal(product[productId].line_item.pre_tax_price)).div(product[productId].line_item.quantity);
        const totalPrice = (new Decimal(product[productId].line_item.pre_tax_price)).sub(unitPrice.mul(quantity));

        await this.setProductPrice({productId, price: totalPrice.div(product[productId].line_item.quantity)});
      }

      // Nothing will ship so don't charge shipping
      if (this.subtotal <= 0.02) {
        await this.setSelected(0);
        await this.setCustomSelected(this.customOptions[0].id);
        this.setCustomSelectedPrice(0);
      }

      // Create a draft order and send to confirmation page
      const initials = this.auth.user.name.split(" ").map((n) => n[0]).join("");
      this.setNote(initials + " - SHORT SHIPPED - " + skus.join(", "));
      await this.refreshPayment();
      await this.createDraftOrder();
    },
    /**
     * Handles the return authorization action.
     * Updates return products and create a draft RA.
     * @returns {Promise<void>}
     */
    async handleRAA() {
      const skus = [];
      const orderedQuantity = _.reduce(this.order.line_items, function (sum, li) {
        return sum + li.quantity;
      }, 0);
      const shortShippedQuantity = _.reduce(this.items, function (sum, li) {
        return sum + li;
      }, 0);
      for (const [lineItemId, quantity] of Object.entries(this.items)) {
        const product = _.pickBy(this.products.items, i => i.line_item.id == lineItemId);
        const productId = Object.keys(product)[0];
        const returnItem = {
          ...this.products.items[productId].original.returnItem, quantity,
        };
        const data = {...this.products.items[productId].original, returnItem: returnItem};
        if (quantity > 0) skus.push(`${quantity}x ` + returnItem.sku);
        this.updateReturnProduct({index: productId, product: data});
      }
      this.setReasonCode("CD");
      this.setReasonCodeDetail("Inventory fill issue");
      this.setRemarks("CF");
      this.setTextRemarks("Refund: SHORT SHIPPED");
      const initials = this.auth.user.name.split(" ").map((n) => n[0]).join("");
      this.setReturnNote(initials + " - SHORT SHIPPED - " + skus.join(", "));
      if (orderedQuantity === shortShippedQuantity) {
        await this.calculateFullRefund();
      } else {
        await this.calculatePartialRefund();
      }
      await this.createDraftRA().then(() => {
        if (this.createRA) {
          this.$router.push({path: 'confirm'});
        } else {
          this.skipRA().then(() => {
            this.$router.push({path: 'success'});
          })
        }
      });
    }
  },
  watch: {
    /**
     * Watches for changes in the products and updates the filtered items.
     */
    products: function () {
      const filteredItems = [];
      this.order.line_items.forEach((i, idx) => {
        const product = _.pickBy(this.products.items, j => j.variant.sku === i.sku);
        const productId = Object.keys(product)[0];
        const isRA = this.$route.path.match(/order-return/);
        if (isRA) {
          const d365Item = extractD365Item(i, this.d365Order.lines, this.products.items[productId]);
          if (d365Item?.SalesOrderLineStatus?.toLowerCase() !== 'invoiced') {
            filteredItems.push(i);
          }
        } else {
          filteredItems.push(i);
        }
      })
      this.filteredItems = filteredItems;
    }
  },
};
</script>

<style>
.short-ship-quantity input {
  pointer-events: none;
}
</style>